import { readonly, ref } from 'vue'

const messageKeys = {
  demo_busy: 'demo.availability.busy',
  demo_reset_in_progress: 'demo.availability.resetting',
  demo_unavailable: 'demo.availability.unavailable',
}

const availabilityCode = ref(null)
let availabilitySession = null
let barrierVersion = 0

export const DEMO_RETRY_READS_EVENT = 'yournutri:demo-retry-reads'

export const getDemoAvailabilityCode = error => {
  const { status, data } = error?.response || {}
  if (status === 423 && ['demo_busy', 'demo_reset_in_progress'].includes(data?.code)) return data.code
  if (status === 503 && data?.code === 'demo_unavailable') return data.code
  return null
}

export const isDemoAvailabilityError = error => getDemoAvailabilityCode(error) !== null
export const getDemoAvailabilityMessageKey = error => messageKeys[getDemoAvailabilityCode(error)] || null

// This volatile state contains only a machine status. The session token is kept
// privately for comparison, never persisted, exposed to components or emitted.
export const demoAvailabilityCode = readonly(availabilityCode)
export const getDemoAvailabilityBarrierVersion = () => barrierVersion

export const clearDemoAvailability = () => {
  availabilityCode.value = null
  availabilitySession = null
  barrierVersion += 1
}

export const synchronizeDemoAvailabilitySession = token => {
  if (availabilitySession && availabilitySession !== token) clearDemoAvailability()
}

export const reportDemoAvailabilityError = (error, token) => {
  const code = getDemoAvailabilityCode(error)
  if (!code || !token) return
  synchronizeDemoAvailabilitySession(token)
  availabilitySession = token
  // A queued busy response must not replace the stronger maintenance notice.
  if (code === 'demo_busy' && ['demo_reset_in_progress', 'demo_unavailable'].includes(availabilityCode.value)) return
  availabilityCode.value = code
  if (code !== 'demo_busy') barrierVersion += 1
}

export const retryDemoReads = () => {
  // Subscribers retry their own failed reads only; no request/body is retained
  // here and no page reload can discard a form draft.
  clearDemoAvailability()
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(DEMO_RETRY_READS_EVENT))
}

export const getDemoAvailabilityKeyForCode = code => messageKeys[code] || null

export const isLocalApiRequest = config => typeof config?.url === 'string'
  && config.url.startsWith('/api/') && !config.baseURL

// Explicit, side-effect-free JSON reads only. In particular, GET invoice/PDF,
// downloads, Google authorization and external catalogue lookups stay excluded.
export const isRetryableDemoRead = config => {
  if (!isLocalApiRequest(config) || (config.method || 'get').toLowerCase() !== 'get'
    || config.demoReadRetried || config.data != null
    || config.cancelToken
    || (config.responseType && config.responseType !== 'json')) return false

  const path = config.url.split(/[?#]/, 1)[0].replace(/\/$/, '')
  return /^\/api\/patient(?:\/[^/]+)?$/.test(path)
    || /^\/api\/meeting\/range\/[^/]+$/.test(path)
    || /^\/api\/(?:meetingconfig|labelmeeting|labeltask|color|serviceinvoice|tariffinvoice|practitioner|therapytopic|dashboard\/stats|health(?:\/settings|\/last)?)$/.test(path)
    || /^\/api\/[^/]+\/health(?:\/last)?$/.test(path)
}
